import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store, UserPagePermission } from 'src//types';
import {
  cleanChosenUser,
  clearSearchUsers,
  createUser,
  deleteUser,
  editUser,
  getUser,
  getUsers,
  selectUser,
} from 'src/modules/users/actions';
import { AddButton } from 'src/components/AddButton';
import { Modal } from 'src/components/Modal';
import UsersInnerModal from './components/UsersInnerModal';
import { useTranslation } from 'react-i18next';
import { ROLES } from 'src/constants/constants';
import Search from './components/Search';
import { useDebounce } from 'react-use';
import { UsersList } from './components/UsersList';
import UsersTable from './components/UsersTable';
import { useIsMobile } from '../../hooks/useIsMobile';
import { DeleteUserModal } from './components/DeleteUserModal';
import { UserPagePermissionModalInner } from './components/UserPagePermissionModalInner';

const DELAY = 500;

type RoleType = keyof typeof ROLES;

const Users: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { appLanguage } = useSelector((state: Store) => state.settings);
  const { loading, users, searchUsers } = useSelector((state: Store) => state.users);

  const [createUserModalVisible, setCreateUserModalVisible] = useState<boolean>(false);
  const [editUserModalVisible, setEditUserModalVisible] = useState<boolean>(false);
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState<boolean>(false);
  const [editPermissionsModalVisible, setEditPermissionsModalVisible] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const allUsers = searchUsers.length > 0 ? searchUsers : users;

  const isMobile = useIsMobile();

  const handleChangeSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [searchValue],
  );

  useDebounce(
    () => {
      const params = {
        username: searchValue.trim(),
      };
      if (searchValue.trim()) {
        dispatch(getUser.request(params));
      }
      if (!searchValue.trim()) {
        dispatch(clearSearchUsers());
      }
    },
    DELAY,
    [searchValue],
  );

  useEffect(() => {
    dispatch(getUsers.request());
  }, [dispatch]);

  const handleHideEditPermissionsModal = useCallback(() => {
    setEditPermissionsModalVisible(false);
  }, []);

  const handleOpenEditPermissionsModal = useCallback(
    (username: string) => {
      const user = allUsers.find((item) => item.username === username);
      if (user) {
        dispatch(selectUser(user));
        setEditPermissionsModalVisible(true);
      }
    },
    [allUsers, dispatch],
  );

  const openCreateUserModal = useCallback(() => {
    dispatch(cleanChosenUser());
    setCreateUserModalVisible(true);
  }, [dispatch]);

  const hideCreateUserModal = useCallback(() => {
    setCreateUserModalVisible(false);
  }, []);

  const hideDeleteUserModal = useCallback(() => {
    setDeleteUserModalVisible(false);
  }, []);

  const openDeleteUserModal = useCallback(
    (username: string) => {
      const user = allUsers.find((item) => item.username === username);
      if (user) {
        dispatch(selectUser(user));
        setDeleteUserModalVisible(true);
      }
    },
    [allUsers, dispatch],
  );

  const handleCreateUser = useCallback(
    (payload: {
      username: string;
      firstName: string;
      lastName: string;
      imageId: number | null;
      authorities: RoleType[];
    }) => {
      dispatch(
        createUser.request({
          requestBody: {
            ...payload,
            language: appLanguage,
          },
          onSuccessCb: () => {
            setCreateUserModalVisible(false);
          },
        }),
      );
    },
    [appLanguage, dispatch],
  );

  const handleDelete = (userId: number) => {
    dispatch(
      deleteUser.request({
        params: {
          userId: userId,
        },
        onSuccessCb: () => {
          setDeleteUserModalVisible(false);
        },
      }),
    );
  };

  const handleOpenEditUserModal = () => {
    setEditUserModalVisible(true);
  };

  const handleSelectUser = useCallback(
    (username: string) => {
      const user = allUsers.find((item) => item.username === username);
      if (user) {
        dispatch(selectUser(user));
        handleOpenEditUserModal();
      }
    },
    [dispatch, allUsers, handleOpenEditUserModal],
  );

  const handleHideEditUserModal = useCallback(() => {
    setEditUserModalVisible(false);
    dispatch(cleanChosenUser());
  }, [dispatch]);

  const handleEditUser = useCallback(
    (payload: {
      username?: string;
      firstName?: string;
      lastName?: string;
      imageId?: number | null;
      authorities?: RoleType[];
      permissions?: UserPagePermission[];
    }) => {
      dispatch(
        editUser.request({
          requestBody: {
            ...payload,
            language: appLanguage,
          },
          onSuccessCb: () => {
            setEditUserModalVisible(false);
            setEditPermissionsModalVisible(false);
            dispatch(clearSearchUsers());
            setSearchValue('');
          },
        }),
      );
    },
    [appLanguage, dispatch],
  );

  return (
    <div>
      <Search searchValue={searchValue} onChange={handleChangeSearch} />

      {isMobile ? (
        <UsersList
          users={allUsers}
          loading={loading}
          onDelete={openDeleteUserModal}
          onEdit={handleSelectUser}
          onEditPermissions={handleOpenEditPermissionsModal}
        />
      ) : (
        <UsersTable
          users={allUsers}
          loading={loading}
          onDelete={openDeleteUserModal}
          onEdit={handleSelectUser}
          onEditPermissions={handleOpenEditPermissionsModal}
        />
      )}

      <AddButton onClick={openCreateUserModal} />
      <Modal
        title={t('users.createUser')}
        isOpen={createUserModalVisible}
        handleClose={hideCreateUserModal}>
        <UsersInnerModal onSubmit={handleCreateUser} onCloseModal={hideCreateUserModal} />
      </Modal>
      <Modal
        title={'Delete user'}
        isOpen={deleteUserModalVisible}
        handleClose={hideDeleteUserModal}>
        <DeleteUserModal onDelete={handleDelete} onClose={hideDeleteUserModal} />
      </Modal>
      <Modal
        title={t('users.editUser')}
        isOpen={editUserModalVisible}
        handleClose={handleHideEditUserModal}>
        <UsersInnerModal isEdit onSubmit={handleEditUser} onCloseModal={handleHideEditUserModal} />
      </Modal>
      <Modal
        isOpen={editPermissionsModalVisible}
        handleClose={handleHideEditPermissionsModal}
        title="Edit permissions">
        <UserPagePermissionModalInner
          onSubmit={handleEditUser}
          onClose={handleHideEditPermissionsModal}
        />
      </Modal>
    </div>
  );
};

export default Users;
